import {
	Box,
	Button,
	Flex,
	Heading,
	HStack,
	chakra,
	Text,
	useBreakpointValue,
	useDisclosure,
	VStack,
	Link,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "../../utils/axios";
import BadgeCard from "../../components/badges/BadgeCard";
import CheckBadgeRankForm from "./BadgeRankForm";
import SubscriptionModal from "../../components/SubscriptionModal";
import { useLocation, useNavigate } from "react-router-dom";
import { formatNumber } from "../../utils/helper";
import { FaTwitter } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from 'react-router-dom';
import { useUser } from "../../context/UserContext";
import moment from "moment";
import HmacSHA256 from "crypto-js/hmac-sha256";

const STR_DEFF = process.env.REACT_APP_STR_DEFF || "";

const MotionHeading = motion(Heading);
const isKeyMintAvailiable = process.env.REACT_APP_MINT_AVAILIABLE === 'true';
const twitterLink = 'https://twitter.com/intent/tweet?text='
const refLink = 'https://scroll.io/canvas/invite/DBQC1';

const BadgeRank = () => {
	const isMobile = useBreakpointValue({ base: true, md: false });
	const [showModal, setShowModal] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [showRank, setShowRank] = useState(false);
	const [rankData, setRankData] = useState(null);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [recentChecks, setRecentChecks] = useState([]);
	const location = useLocation();
	const { user, loadingUser } = useUser();

	const twitterText = rankData && encodeURIComponent(`My Scroll Badges Rank is ${formatNumber(rankData.badgeRank)}. Find out where you stand (by @scrollstat) https://scrollstat.xyz/check-badges-rank`);


	const handleSubmit = async (values, { setSubmitting, setErrors }) => {
		const twitterSubscribed = localStorage.getItem('twitterSubscribed') === 'true';
		const telegramSubscribed = localStorage.getItem('telegramSubscribed') === 'true';
		const twitterFounderSubscribed = localStorage.getItem('twitterFounderSubscribed') === 'true';
		const postRetweeted = localStorage.getItem('retweetCompleted') === 'true';
		const discordInvited = localStorage.getItem('discordSubscribed') === 'true';
		const hasCompletedSubscription = localStorage.getItem('hasCompletedSubscription') === 'true' && twitterSubscribed && telegramSubscribed && twitterFounderSubscribed && postRetweeted && discordInvited;

		if (!hasCompletedSubscription) {
			setShowModal(true);
			onOpen();
			setSubmitting(false);
			return;
		}

		try {
			const address = values.address;
			await fetchBadgesRankData(address);
		} catch (error) {
			setErrors({
				address: 'There is currently high traffic on the site due to many users. Please try again later.'
			});
			setRankData(null);
		} finally {
			setSubmitting(false);
		}
	};

	const fetchBadgesRankData = async (address) => {
		setLoading(true);
		try {
			const isUserHasKey = !loadingUser && user && user.mintedKey;

			const lastFetchBadgesTime = localStorage.getItem('lastFetchBadgesTime');
			const currentTime = new Date().getTime();

			if (!isUserHasKey && lastFetchBadgesTime && currentTime - lastFetchBadgesTime < 3600000) {
				const remainingTime = 3600000 - (currentTime - lastFetchBadgesTime); // Time remaining in milliseconds
				const remainingMinutes = Math.floor((remainingTime / 1000) / 60);
				const remainingSeconds = Math.floor((remainingTime / 1000) % 60);

				setError(`Please wait ${remainingMinutes} minute(s) and ${remainingSeconds} second(s) before checking again or mint key.`);
				return;
			}
			setError('');

			const formattedDate = moment.utc().add(2, 'minutes').format('DD.MM.YYYY HH:mm'); // Добавляем 2 минуты и форматируем без секунд
			const stringifiedData = JSON.stringify({ address, formattedDate });
			const hmac = HmacSHA256(stringifiedData, STR_DEFF).toString();

			const response = await api.post('/badges/check-rank', { address }, {
				headers: {
					"Content-Type": "application/json",
					"X-Signature": hmac,
				},
			});

			const newRankData = response.data;

			const newBadgeCheck = {
				address,
				badgeCount: newRankData.badgesCount,
				rank: newRankData.badgeRank,
				percentage: newRankData.percentage,
			};

			let updatedBadgesChecks = [...recentChecks];
			updatedBadgesChecks = updatedBadgesChecks.filter(check => check.address !== address);
			updatedBadgesChecks.push(newBadgeCheck);
			if (updatedBadgesChecks.length > 5) {
				updatedBadgesChecks = updatedBadgesChecks.slice(-5);
			}

			setRecentChecks(updatedBadgesChecks);
			localStorage.setItem('recentBadgesChecks', JSON.stringify(updatedBadgesChecks));
			localStorage.setItem('lastFetchBadgesTime', currentTime.toString());
			setRankData(newRankData);
			setError('');
			setShowRank(true);
		} catch (error) {
			setError('There is currently high traffic on the site due to many users. Please try again later.');
			setRankData(null);
		} finally {
			setLoading(false);
		}
	};
	const handleCloseModal = () => {
		const twitterSubscribed = localStorage.getItem('twitterSubscribed') === 'true';
		const telegramSubscribed = localStorage.getItem('telegramSubscribed') === 'true';
		const twitterFounderSubscribed = localStorage.getItem('twitterFounderSubscribed') === 'true';
		const postRetweeted = localStorage.getItem('retweetCompleted') === 'true';
		const discordInvited = localStorage.getItem('discordSubscribed') === 'true';
		if (twitterSubscribed && telegramSubscribed && twitterFounderSubscribed && postRetweeted && discordInvited) {
			localStorage.setItem('hasCompletedSubscription', 'true');
		}
		setShowModal(false);
		onClose();
	};

	const handleTwitterSubscribe = () => {
		localStorage.setItem('twitterSubscribed', 'true');
	};

	const handleTelegramSubscribe = () => {
		localStorage.setItem('telegramSubscribed', 'true');
	};

	const handleTwitterFounderSubscribe = () => {
		localStorage.setItem('twitterFounderSubscribed', 'true');
	};

	const handleTwitterPostRetweet = () => {
		localStorage.setItem('retweetCompleted', 'true');
	};

	const handleDiscordInvite = () => {
		localStorage.setItem('discordSubscribed', 'true');
	};

	const handleReset = () => {
		setLoading(true);
		setShowRank(false);
		setRankData(null);
		setError('');
		navigate('/check-badges-rank');
		setLoading(false);
	};

	const handleClearRecentChecks = () => {
		setRecentChecks([]);
		localStorage.removeItem('recentBadgesChecks');
	};

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const address = queryParams.get('address');

		if (address) {
			if (shouldShowSubscriptionModal()) {
				setShowModal(true);
				onOpen();
			}
		}

		const savedChecks = JSON.parse(localStorage.getItem('recentBadgesChecks')) || [];
		setRecentChecks(savedChecks);
	}, [location.search]);

	const shouldShowSubscriptionModal = () => {
		const twitterSubscribed = localStorage.getItem('twitterSubscribed') === 'true';
		const telegramSubscribed = localStorage.getItem('telegramSubscribed') === 'true';
		const twitterFounderSubscribed = localStorage.getItem('twitterFounderSubscribed') === 'true';
		const postRetweeted = localStorage.getItem('retweetCompleted') === 'true';

		const savedBadgesChecks = JSON.parse(localStorage.getItem('recentBadgesChecks')) || [];
		return savedBadgesChecks.length > 1 && !localStorage.getItem('hasCompletedSubscription') && !twitterSubscribed && !telegramSubscribed && !twitterFounderSubscribed && !postRetweeted;
	};

	return (
		<>
			<Helmet>
				<title>Check Badges Rank</title>
				<meta name="twitter:image" content="https://scrollstat.xyz/assets/images//scroll-checker-badge-og-image.png" />
			</Helmet>
			<Box padding={4}>
				<VStack align="center">
					<Heading
						fontSize="clamp(1.5em, 2.8vw, 2.4em)"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 1 }}
						textAlign={"center"}
						mb={!isMobile ? 6 : 1}
					>
						Check Badges Rank
					</Heading>
					{!showRank ? <Box w={isMobile ? '100%' : "700px"}>
						<CheckBadgeRankForm
							handleSubmit={handleSubmit}
							isKeyMintAvailiable={isKeyMintAvailiable}
							serverError={error}
							recentChecks={recentChecks}
							handleClearRecentChecks={handleClearRecentChecks}
						/>
					</Box> :
						<Box w="100%" textAlign={"center"} px={4} overflowX="hidden" maxW={"700px"}>
							<Box
								p={8}
								borderWidth={0}
								borderRadius="lg"
								textAlign="center"
								backgroundColor="#FEF0DD"
								// boxShadow="lg"
								maxWidth="100%"
								minWidth="300px"
								mx="auto"
							>
								<Text fontSize={{ base: "4xl", md: "7xl" }} fontWeight="bold" color="#FF684B">
									#{formatNumber(rankData.badgeRank)}

								</Text>
								<Text color={"gray.700"}>
									Your wallet is in <chakra.span color="#FF5733" fontWeight={"bold"}>Top {Math.ceil(rankData.percentage)}%</chakra.span> and better than {formatNumber(rankData.totalUsers - rankData.badgeRank)} of {formatNumber(rankData.totalUsers)} users.
								</Text>
								<Text fontSize={{ base: "2xl", md: "4xl" }} color="teal.700" mb={2} pt={4}>
									<Text as="span" fontWeight="bold">{rankData.badgesCount} badges</Text>
								</Text>

								{rankData.badgesCount === 0 &&
									<Text color={"gray.700"} mb={6} className="heartbeat-slow">
										No badges yet. <chakra.span><Link isExternal href={refLink} target="_blank" color="#FF5733" >Mint your Scroll Canvas at 50% off!</Link></chakra.span>
									</Text>
								}


								<Link m2={2} isExternal href={twitterLink + twitterText} variant={"outline"} colorScheme="twitter" opacity={0.75} style={{ textDecoration: 'none' }}>
									<Button as="div" variant="outline" colorScheme="twitter" rightIcon={<FaTwitter />} size={'md'}>Share on Twitter</Button>
								</Link>

							</Box>

							<Text align="center" pt={4}>
								What's your marks rank?{" "}
								<Link as={RouterLink} to="/check-marks-rank">
									<chakra.span color="#FF5733" _hover={{ textDecoration: "underline" }}>
										Check now!
									</chakra.span>
								</Link>
							</Text>


							<Flex mt={8} gap={4} justifyContent={"center"} wrap="wrap">
								<Button background="#FF684B" color="white" _hover={{ background: "#FF5733" }} height="48px" onClick={handleReset}>Check Another Address</Button>
							</Flex>
						</Box>
					}
				</VStack>
			</Box>
			<Box pt={4}>
				{showRank && <Text textAlign={"center"}><chakra.span color="#FF5733">Note:</chakra.span> Not all badges you minted may be highlighted.</Text>}
				<VStack spacing={8} align="center" pt={8} pb={8}>
					<HStack wrap="wrap" spacing={4} justify="center" alignItems="stretch">
						{!loading && showRank && rankData && rankData.badges.map((badge, index) => (
							<motion.div
								key={index}
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.5 }}
							>
								<BadgeCard badge={badge} isMobile={isMobile} />
							</motion.div>
						))}
					</HStack>
				</VStack>
			</Box>


			{showModal && (
				<SubscriptionModal
					isOpen={isOpen}
					onClose={handleCloseModal}
					onTwitterSubscribe={handleTwitterSubscribe}
					onTelegramSubscribe={handleTelegramSubscribe}
					onTwitterFounderSubscribe={handleTwitterFounderSubscribe}
					onTwitterPostRetweet={handleTwitterPostRetweet}
					onDiscordInvite={handleDiscordInvite}
				/>
			)}
		</>
	);
};

export default BadgeRank;
